import { injectable, inject } from 'inversify';
import { Project } from "../../domain/aggregates/Project";
import { ProjectRepository } from "../../infrastructure/repositories/ProjectRepository";
import { 
  CreateProjectCommand, 
  CreateProjectResult 
} from "../commands/CreateProjectCommand";
import { TYPES } from '../../../../types/DITypes';

/**
 * Project Application Service
 * 
 * Following Eric Evans Application Service pattern for project management.
 * Coordinates project operations following GTD principles.
 * 
 * GTD Rule: Every project must have a clear desired outcome and at least one next action.
 */
@injectable()
export class ProjectApplicationService {
  constructor(
    @inject(TYPES.ProjectRepository) private readonly projectRepository: ProjectRepository
  ) {}

  /**
   * Create a new GTD project
   * 
   * GTD Principle: Projects come from processing inbox items or direct creation
   * for multi-step outcomes.
   */
  async createProject(command: CreateProjectCommand): Promise<CreateProjectResult> {
    try {
      // Create the project aggregate
      const project = Project.create(
        command.name,
        command.desiredOutcome,
        command.createdBy,
        command.area,
        command.reviewDate
      );

      // Persist the project
      await this.projectRepository.save(project);

      return {
        success: true,
        projectId: project.id,
        createdAt: project.createdAt.toISOString(),
        message: `Project "${command.name}" created successfully`
      };
    } catch (error) {
      return {
        success: false,
        projectId: '',
        createdAt: new Date().toISOString(),
        message: error instanceof Error ? error.message : 'Failed to create project'
      };
    }
  }

  /**
   * Complete a project
   * 
   * GTD: Mark project as completed when desired outcome is achieved
   */
  async completeProject(projectId: string, completedBy: string): Promise<{ success: boolean; message: string }> {
    try {
      const project = await this.projectRepository.findById(projectId);
      
      if (!project) {
        return {
          success: false,
          message: `Project ${projectId} not found`
        };
      }

      project.complete(completedBy);
      await this.projectRepository.save(project);

      return {
        success: true,
        message: `Project completed successfully`
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to complete project'
      };
    }
  }

  /**
   * Defer project to Someday/Maybe
   * 
   * GTD: Move project out of active list but keep for future consideration
   */
  async deferProject(projectId: string): Promise<{ success: boolean; message: string }> {
    try {
      const project = await this.projectRepository.findById(projectId);
      
      if (!project) {
        return {
          success: false,
          message: `Project ${projectId} not found`
        };
      }

      project.defer();
      await this.projectRepository.save(project);

      return {
        success: true,
        message: `Project moved to Someday/Maybe list`
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to defer project'
      };
    }
  }
}